import React, { useEffect, useCallback } from 'react';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

/**
 * BusinessNavigationGuard - SaaS Business Navigation Control
 * 
 * Bu component SaaS uygulamalarında standart olan business-scoped navigation 
 * kontrolünü sağlar. Kullanıcıların yetkisi olmayan business'lara erişimini 
 * engeller ve doğru business context'ini korur.
 * 
 * SaaS Best Practices:
 * - URL structure: /business/{businessId}/module/page
 * - Business context validation
 * - Unauthorized access prevention
 * - Persistent authentication within business scope
 * - Browser navigation control (back/forward)
 */
const BusinessNavigationGuard = ({ children }) => {
  const {
    currentUser,
    selectedBusiness,
    userBusinesses = [],
    isAuthenticated,
    currentUserRole,
    switchBusiness,
    logout,
    isLoading
  } = useAuthBusiness();
  const navigate = useNavigate();

  /**
   * Extract business ID from URL
   * Supports patterns like:
   * - /business/{businessId}/dashboard
   * - /sectors/{businessId}/food-beverage/dashboard
   */
  const extractBusinessIdFromUrl = useCallback(() => {
    const path = window.location.pathname;

    // Sabit modül veya yönetim rotalarını businessId olarak algılamayı engelle
    const reservedBusinessPaths = [
      'whatsapp', 
      'integration', 
      'integrations', 
      'select', 
      'settings', 
      'dashboard', 
      'profile', 
      'billing',
      'franchise-analytics',
      'franchise-network-map'
    ];

    // Check for explicit business ID in URL
    const businessMatch = path.match(/\/business\/([^\/]+)/);
    if (businessMatch) {
      if (!reservedBusinessPaths.includes(businessMatch[1])) {
        return businessMatch[1];
      }
    }

    // Check for B2B portal path /sectors/food-beverage/b2b/{businessId}/portal
    const b2bMatch = path.match(/\/b2b\/([^\/]+)/);
    if (b2bMatch) {
      return b2bMatch[1];
    }

    // Check for sectoral business pages
    const sectorMatch = path.match(/\/sectors\/([^\/]+)\//);
    if (sectorMatch && 
        sectorMatch[1] !== 'food-beverage' && 
        sectorMatch[1] !== 'restaurant' && 
        sectorMatch[1] !== 'retail' && 
        sectorMatch[1] !== 'service') {
      return sectorMatch[1];
    }

    return null;
  }, []);

  /**
   * Validate business access for current user
   */
  const validateBusinessAccess = useCallback((businessId) => {
    if (!businessId) return true; // Public or general pages

    // 🛡️ SYNC FIX: If we have a token in localStorage but state hasn't caught up, wait.
    let hasToken = false;
    let isLoadingLocal = false;
    try { 
      hasToken = !!localStorage.getItem('token'); 
      isLoadingLocal = localStorage.getItem('auth_loading') === 'true';
    } catch (e) {}
    
    if (hasToken && (!isAuthenticated || !currentUser)) {
      console.log('⏳ Navigation guard - Auth state pending but token exists. Waiting...');
      return true; // Allow for now to prevent loop to landing
    }

    if (!isAuthenticated || !currentUser) {
      console.log('🚫 Navigation guard - User not authenticated');
      return false;
    }

    // Admin users have access to all businesses
    if (currentUser.user_type === 'system_admin' ||
      currentUser.user_type === 'admin' ||
      currentUser.role === 'super_admin') {
      console.log('✅ Navigation guard - Admin user, allowing access');
      return true;
    }

    // Check if user has access to this business (with localStorage fallbacks for race conditions)
    const storedSelectedBusiness = (() => {
      try { return JSON.parse(localStorage.getItem('selectedBusiness') || 'null'); } catch { return null; }
    })();
    const storedSelectedBusinessId = localStorage.getItem('selectedBusinessId');

    const normalize = (str) => {
      if (!str) return '';
      const trMap = { 'ç': 'c', 'Ç': 'c', 'ğ': 'g', 'Ğ': 'g', 'ı': 'i', 'I': 'i', 'İ': 'i', 'ö': 'o', 'Ö': 'o', 'ş': 's', 'Ş': 's', 'ü': 'u', 'Ü': 'u' };
      return str.split('').map(c => trMap[c] || c).join('').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
    };

    const targetKey = normalize(businessId);

    const hasAccess = (userBusinesses || []).some(business =>
      business.id === businessId ||
      business.slug === businessId ||
      business.business_id === businessId ||
      normalize(business.slug) === targetKey ||
      normalize(business.name || business.business_name) === targetKey
    ) || (
      storedSelectedBusiness && (
        storedSelectedBusiness.id === businessId ||
        storedSelectedBusiness.slug === businessId ||
        storedSelectedBusiness.business_id === businessId ||
        normalize(storedSelectedBusiness.slug) === targetKey ||
        normalize(storedSelectedBusiness.name || storedSelectedBusiness.business_name) === targetKey
      )
    ) || (
      storedSelectedBusinessId === businessId
    );

    if (!hasAccess) {
      console.log('🚫 Navigation guard - User does not have access to business:', businessId);
      console.log('🔍 Available businesses:', (userBusinesses || []).map(b => ({ id: b.id, slug: b.slug })));
      return false;
    }

    return true;
  }, [isAuthenticated, currentUser, userBusinesses]);

  /**
   * Handle unauthorized access
   */
  const handleUnauthorizedAccess = useCallback((businessId) => {
    console.warn('🚫 Unauthorized business access attempt:', businessId);

    // Redirect to appropriate landing page based on user type
    if (currentUser?.user_type === 'system_admin' || currentUser?.user_type === 'admin') {
      navigate('/dashboard/admin/dashboard');
    } else if (userBusinesses?.length > 0) {
      // Redirect to first available business
      const firstBusiness = userBusinesses[0];
      switchBusiness(firstBusiness);
    } else {
      // Redirect to business selection or dashboard
      navigate('/dashboard/personal');
    }
  }, [currentUser, userBusinesses, switchBusiness, navigate]);

  /**
   * Main navigation guard logic
   */
  const checkNavigation = useCallback(() => {
    if (isLoading) {
      console.log('⏳ Navigation guard - Auth loading, skipping check');
      return;
    }

    const path = window.location.pathname;
    const publicRoutes = ['/login', '/register', '/forgot-password', '/', '/landing', '/menu', '/hotel', '/sectorgo', '/sectorgo/login', '/sectors/food-beverage/sectorgo', '/sectors/food-beverage/sectorgo/login', '/sectors/food-beverage/courier-app'];

    const effectiveRole = localStorage.getItem('userRole') || currentUserRole || currentUser?.role || currentUser?.user_type || '';

    // 🚀 AUTH SYNC: If we have a token but no user yet, wait for AuthBusinessContext to finish
    let hasToken = false;
    try { hasToken = !!localStorage.getItem('token'); } catch (e) {}
    
    if (hasToken && (!isAuthenticated || !currentUser)) {
      console.log('⏳ Navigation guard - Authenticated but user data missing. Waiting...');
      return;
    }

    // 🛡️ FAZ 1: CARI / B2B KİLİDİ (Native App Strategy)
    // Cari kullanıcıların public sayfalara veya dashboard'lara erişmesini engelle, doğrudan B2B Portalına kilitle.
    if (isAuthenticated && effectiveRole === 'cari') {
      const allowedCariPaths = [
        '/sectors/food-beverage/operations/distribution',
        '/sectors/food-beverage/b2b/', // B2B Portal prefix
        '/profile',
      ];
      
      const isAllowedPath = allowedCariPaths.some(allowed => path === allowed || path.startsWith(allowed));
      
      if (!isAllowedPath) {
        console.log('🎯 Navigation guard - Restricting cari from unauthorized path:', path);
        
        let targetBizId = null;
        if (userBusinesses && userBusinesses.length > 0) {
            const cariMembership = userBusinesses.find(m => m.role === 'cari' || m.type === 'b2b_client') || userBusinesses[0];
            targetBizId = cariMembership?.business_id || cariMembership?.id;
        }

        if (targetBizId) {
            navigate(`/sectors/food-beverage/b2b/${targetBizId}/portal`, { replace: true });
        } else {
            navigate('/sectors/food-beverage/operations/distribution', { replace: true });
        }
        return;
      }

      // 🔄 EĞER B2B PORTAL VEYA SEKTÖREL PATH'İNDEYSEK VE URL'DE BUSINESS ID VARSA CONTEXT'İ GÜNCELLE
      const businessId = extractBusinessIdFromUrl();
      if (businessId) {
        if (selectedBusiness?.id !== businessId &&
            selectedBusiness?.slug !== businessId &&
            selectedBusiness?.business_id !== businessId) {
          const targetBusiness = userBusinesses.find(b =>
            b.id === businessId || b.slug === businessId || b.business_id === businessId
          );
          if (targetBusiness) {
            console.log('🔄 Navigation guard (Cari) - Switching to URL business:', targetBusiness);
            switchBusiness(targetBusiness);
          }
        }
      }

      return; // İzin verilen cari sayfalarına (B2B Portal) erişime devam.
    }

    // 🚚 COURIER REDIRECT: Prevent couriers from accessing normal dashboard areas or other terminals
    if (isAuthenticated && effectiveRole === 'courier') {
      if (!path.includes('/courier-app') && !path.includes('/sectorgo')) {
        console.log('🎯 Navigation guard - Redirecting courier to courier app');
        const m = userBusinesses?.[0];
        const sector = (m?.sector || 'food-beverage').toLowerCase().replace('_', '-');
        navigate(`/sectors/${sector}/courier-app`, { replace: true });
        return;
      }
      return; // Allow access to courier-app and sectorgo
    }

    // 🍳 CHEF / KITCHEN STAFF REDIRECT: Prevent chefs from accessing normal panels, force them to production terminal
    if (isAuthenticated && (effectiveRole === 'chef' || effectiveRole === 'kitchen_staff')) {
      const allowedChefPaths = [
        '/uretim',
        '/profile',
        '/courier-app'
      ];
      const isAllowed = allowedChefPaths.some(allowed => path.endsWith(allowed) || path.includes(allowed));
      if (!isAllowed) {
        console.log('🎯 Navigation guard - Redirecting chef to production terminal');
        const m = userBusinesses?.[0];
        const storedSelectedBusiness = (() => {
          try { return JSON.parse(localStorage.getItem('selectedBusiness') || 'null'); } catch { return null; }
        })();
        const targetSlug = m?.slug || storedSelectedBusiness?.slug || m?.business_id || localStorage.getItem('selectedBusinessId') || 'madem-tatli';
        navigate(`/${targetSlug}/uretim`, { replace: true });
        return;
      }
      return; // Allow access to uretim
    }

    const isWholesalePath = path.endsWith('/toptan') || path.includes('/toptan/') || path.split('/').includes('toptan') || 
                            path.endsWith('/uretim') || path.includes('/uretim/') || path.split('/').includes('uretim');

    const isPublicRoute = publicRoutes.some(route => route === '/' ? path === '/' : (path === route || path.startsWith(route)));
    const isCustomDomain = !window.location.hostname.includes('prmanager') && !window.location.hostname.includes('localhost') && window.location.hostname.includes('.');

    const isSectorGoPath = path.includes('/sectorgo') || path.includes('/courier-app');

    if (path.startsWith('/menu') || isPublicRoute || isWholesalePath || isCustomDomain || isSectorGoPath) {
      return;
    }

    // Skip guard for admin routes (handled by admin auth)
    if (path.startsWith('/dashboard/admin')) {
      return;
    }

    const businessId = extractBusinessIdFromUrl();

    // If URL contains business ID, validate access
    if (businessId) {
      if (!validateBusinessAccess(businessId)) {
        handleUnauthorizedAccess(businessId);
        return;
      }

      // /business/... yönetim rotalarında gereksiz yere dashboard'a yönlendirmeyi önle
      if (!path.startsWith('/business/')) {
        // Ensure selected business matches URL business
        if (selectedBusiness?.id !== businessId &&
          selectedBusiness?.slug !== businessId &&
          selectedBusiness?.business_id !== businessId) {

          const targetBusiness = userBusinesses.find(b =>
            b.id === businessId || b.slug === businessId || b.business_id === businessId
          );

          if (targetBusiness) {
            console.log('🔄 Navigation guard - Switching to URL business:', targetBusiness);
            // Update context without navigation (we're already on the right page)
            switchBusiness(targetBusiness);
          }
        }
      }
    }

    // For sectoral pages without explicit business ID in URL,
    // ensure user has a selected business
    else if (path.startsWith('/sectors/') && !selectedBusiness) {
      // 🛡️ REFRESH FIX: Safe check for re-hydration
      const storedBusinessId = localStorage.getItem('selectedBusinessId');
      
      if (hasToken || isAuthenticated) {
         console.log('⏳ Navigation guard - Stored business id or businesses found, waiting for selection re-hydration...');
         return;
      }
      
      console.log('🔄 Navigation guard - Sectoral page needs business selection', { path, hasToken, storedBusinessId, businessCount: userBusinesses.length });

      if (userBusinesses.length === 1) {
        // Auto-select if only one business
        switchBusiness(userBusinesses[0]);
      } else if (userBusinesses.length > 1) {
        // Redirect to business selection
        navigate('/business/select');
      } else if (!hasToken && !isAuthenticated) {
        // No token, no business - go to login
        navigate('/login');
      } else {
        // User is logged in but has no businesses or they failed to load
        console.warn('⚠️ Navigation guard - Logged in user has no accessible businesses');
      }
    }

  }, [extractBusinessIdFromUrl, validateBusinessAccess, handleUnauthorizedAccess, selectedBusiness, userBusinesses, switchBusiness, isLoading, currentUserRole, isAuthenticated, navigate, currentUser]);

  /**
   * Monitor URL changes and validate navigation
   */
  useEffect(() => {
    // Check initial load
    checkNavigation();

    // Listen for browser navigation (back/forward buttons)
    const handlePopState = () => {
      console.log('🎯 Navigation guard - Browser navigation detected');
      setTimeout(checkNavigation, 100); // Small delay to ensure URL is updated
    };

    // Listen for programmatic navigation
    const handleRouteChange = () => {
      console.log('🎯 Navigation guard - Route change detected');
      checkNavigation();
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('routeChanged', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('routeChanged', handleRouteChange);
    };
  }, [checkNavigation]);

  /**
   * Override browser navigation to maintain business context
   */
  useEffect(() => {
    // Store original pushState and replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Override pushState to validate business access safely
    window.history.pushState = function (state, title, url) {
      try {
        originalPushState.apply(this, arguments);
      } catch (e) {
        console.warn('pushState error:', e);
      }
      window.dispatchEvent(new Event('routeChanged'));
    };

    // Override replaceState to validate business access  
    window.history.replaceState = function (state, title, url) {
      try {
        originalReplaceState.apply(this, arguments);
      } catch (e) {
        console.warn('replaceState error:', e);
      }
      window.dispatchEvent(new Event('routeChanged'));
    };

    // Cleanup: restore original functions
    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  /**
   * Session persistence: Kullanıcı kendi isteğiyle çıkış yapmadıkça
   * oturum süresiz ve kesintisiz olarak açık kalır (Local Agent EXE, PWA, Mobil ve Web dahil).
   */

  const path = window.location.pathname;
  const isSectorGo = path.includes('/sectorgo') || path.includes('/courier-app');
  const isSectoralPath = path.startsWith('/sectors/') && !isSectorGo;
  
  // 🛡️ INCOGNITO SAFE STORAGE ACCESS:
  const hasToken = (() => {
    try { return !!localStorage.getItem('token'); } catch (e) { return false; }
  })();
  
  const storedBusinessId = (() => {
    try { return localStorage.getItem('selectedBusinessId'); } catch (e) { return null; }
  })();

  // 🛡️ RE-HYDRATION SAFETY RENDER:
  const storedBusinessObj = (() => {
    try { return JSON.parse(localStorage.getItem('selectedBusiness') || 'null'); } catch { return null; }
  })();

  if (isSectoralPath && hasToken && !selectedBusiness) {
    // 🚀 AUTO-HEAL: Eğer kullanıcının işletmesi varsa ama selectedBusiness null kaldıysa otomatik ilkini seç
    if (userBusinesses && userBusinesses.length > 0 && typeof switchBusiness === 'function') {
      const defaultBiz = storedBusinessObj || userBusinesses[0];
      console.log('⚡ Auto-healing selectedBusiness in Guard:', defaultBiz);
      switchBusiness(defaultBiz);
    }

    // Eğer localStorage'da işletme varsa veya isLoading bittiyse engelleme, çocukları göster
    if (storedBusinessObj?.id || !isLoading) {
      return <>{children}</>;
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50/50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4 opacity-40"></div>
            <p className="text-gray-400 text-xs font-black uppercase tracking-widest animate-pulse">
              Yetkiler Senkronize Ediliyor...
            </p>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      {children}
    </>
  );
};

export default BusinessNavigationGuard;